import React from 'react';
import { FormattedMessage } from 'react-intl';
import ROUTES from '../../Routes/constants';
import { TradeState } from '../../../_shared/types/trade';
import Loading from '../../../_shared/components/Loading';
import Button from '../../../_shared/components/Button';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import BadgeWithTooltip from '../../../_shared/components/BadgeWithTooltip';
import Icon from '../../../_shared/components/Icon';
import { BidSource } from '../../../_shared/types/auction';
import StyledAnchorLink from '../../../_shared/components/AnchorLink';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import useAnalytics, {
  getGenericBiddingListProperties,
  getVinBasicProperties,
  getVinLaunchProperties,
  getSellerProperties
} from '../../../_shared/hooks/useAnalytics';

import {
  StyledSellerInfo,
  StyledSellerHeading,
  StyledSellerSubHeading,
  StyledBox,
  StyledDealerBox,
  StyledQABox,
  StyledDealer,
  StyledRatings,
  StyledNoRatings,
  StyledRatingsCount,
  StyledIcon,
  StyledNetworkBox,
  StyledPaymentBox,
  StyledDealerLoading,
  StyledNetworkName,
  StyledIconContainer
} from './Styles';

const SellerInfo = ({
  acceptedPayments,
  address,
  appraisedBy,
  chatCount,
  dealerId,
  isCsu,
  network,
  paymentMethods,
  rating,
  ratingsCount,
  tradeId,
  badges,
  tradeState,
  isOMP,
  tradeDetails
}) => {
  const track = useAnalytics();
  const location = window.location.href.includes(ROUTES.BUYING)
    ? BidSource.MINI_VDP
    : BidSource.VDP_OVERLAY;

  // Show the Payment Method section if there is more than one method OR if the one method is not a form of TR Pay
  const shouldShowPayments = () => {
    if (
      acceptedPayments.length > 1 ||
      (acceptedPayments.length === 1 &&
        !acceptedPayments[0].key.toLowerCase().includes('traderev'))
    ) {
      return true;
    }
    return false;
  };

  const goToQA = () => {
    if (isOMP) {
      track(SegmentConfig.names.viewQA, {
        ...getSellerProperties(tradeDetails),
        ...getVinBasicProperties(tradeDetails),
        ...getVinLaunchProperties(tradeDetails),
        ...getGenericBiddingListProperties(tradeDetails),
        is_qa_feed_available: chatCount > 0
      });

      window.open(`${ROUTES.OMP_ENDPOINT}trade/${tradeId}/qa`, '_self');
    } else {
      track(MixpanelConfig.names.viewQA, {
        qa_feed_available: chatCount > 0,
        source: location
      });

      window.open(`${ROUTES.TR4_ENDPOINT}trade/${tradeId}/qa`, '_self');
    }
  };

  const trackDealerProfileView = () => {
    track(MixpanelConfig.names.viewDealerProfilePage, {
      source: `seller info - ${location}`
    });
  };

  const trackDealerProfileViewOmp = () => {
    track(SegmentConfig.names.viewDealerProfilePage, {
      ...getVinBasicProperties(tradeDetails),
      ...getSellerProperties(tradeDetails)
    });
  };

  return (
    <StyledSellerInfo data-testid="seller" id="seller">
      <StyledSellerHeading
        id="transportDetailsSection"
        data-testid="vdpSellerInfo-title"
      >
        {/* Seller info */}
        <FormattedMessage id="vdp.seller.heading" />
        {network ? (
          network.isDealerGroupNetwork && (
            <StyledNetworkBox>
              <StyledIconContainer>
                <Icon icon="TR5-GroupNetwork" />
              </StyledIconContainer>
              <StyledSellerSubHeading>
                {/* Network: */}
                <FormattedMessage id="vdp.seller.network" />
              </StyledSellerSubHeading>
              <StyledNetworkName
                alt={network.displayName}
                className="notranslate"
              >
                {network.displayName}
              </StyledNetworkName>
            </StyledNetworkBox>
          )
        ) : (
          <Loading width="150px" height="45px" />
        )}
      </StyledSellerHeading>
      <StyledBox>
        <StyledDealerBox>
          <StyledSellerSubHeading>
            {/* Selling dealer */}
            <FormattedMessage id="vdp.seller.dealer" />
          </StyledSellerSubHeading>
          {appraisedBy &&
          address &&
          rating &&
          dealerId &&
          Number.isInteger(ratingsCount) ? (
            <>
              <StyledDealer className="notranslate">
                {!isOMP ? (
                  <StyledAnchorLink
                    href={`${process.env.REACT_APP_TR4_ENDPOINT}profile/${tradeId}/dealer/${dealerId}`}
                    rel="noopener noreferrer"
                    onClick={trackDealerProfileView}
                    data-testid="vdp-seller-info"
                  >
                    {isCsu
                      ? `${appraisedBy.fullName} - ${appraisedBy.companyName}`
                      : `${appraisedBy.companyName}`}
                  </StyledAnchorLink>
                ) : (
                  <StyledAnchorLink
                    href={`${process.env.REACT_APP_OMP_ENDPOINT}profile/${tradeId}/dealer/${dealerId}`}
                    rel="noopener noreferrer"
                    onClick={trackDealerProfileViewOmp}
                    data-testid="vdp-seller-info"
                  >
                    {appraisedBy.companyName}
                  </StyledAnchorLink>
                )}
                <span>
                  {`${address.city}, ${address.province}, ${address.postalCode}`}
                </span>
              </StyledDealer>
              {ratingsCount > 0 ? (
                <StyledRatings>
                  {rating}
                  <StyledIcon icon="TR5-Icon-Star-Filled" />
                  <StyledRatingsCount>
                    {/* # ratings */}
                    <FormattedMessage
                      id="vdp.seller.ratings"
                      values={{ ratingsCount }}
                    />
                  </StyledRatingsCount>
                </StyledRatings>
              ) : (
                <StyledNoRatings className="notranslate">
                  {/* (No ratings yet) */}
                  <FormattedMessage id="vdp.seller.noRatings" />
                </StyledNoRatings>
              )}
              <BadgeWithTooltip badges={badges} />
            </>
          ) : (
            <StyledDealerLoading>
              <Loading width="200px" height="50px" />
              <Loading width="100px" height="20px" />
            </StyledDealerLoading>
          )}
        </StyledDealerBox>
        {Number.isInteger(chatCount) ? (
          <StyledQABox>
            {tradeState !== TradeState.upcoming && (
              <>
                <StyledSellerSubHeading>
                  {/* Questions & answers */}
                  <FormattedMessage id="vdp.seller.qa" />
                </StyledSellerSubHeading>
                {chatCount > 0 && (
                  <p>
                    {/* # answers from seller */}
                    <FormattedMessage
                      id="vdp.seller.answers"
                      values={{ chatCount }}
                    />
                  </p>
                )}
                <Button.Secondary onClick={goToQA}>
                  {/* Open Q&A */}
                  <FormattedMessage id="vdp.seller.qaButton" />
                </Button.Secondary>
              </>
            )}
          </StyledQABox>
        ) : (
          <Loading width="160px" height="90px" />
        )}
        {!acceptedPayments ? (
          <Loading width="150px" height="45px" />
        ) : (
          shouldShowPayments() && (
            <StyledPaymentBox>
              <StyledSellerSubHeading>
                {/* Accepted payments */}
                <FormattedMessage id="vdp.seller.payments" />
              </StyledSellerSubHeading>
              <p>{paymentMethods.join(', ')}</p>
            </StyledPaymentBox>
          )
        )}
      </StyledBox>
    </StyledSellerInfo>
  );
};

export default SellerInfo;
